/**
 * Small form primitives for the Apprise VM section. They reuse the wizard's
 * own CSS module so the section looks identical to the rest of step 3.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import styles from '@/pages/NewDeploymentPage.module.css'

interface FieldShellProps {
  label: string
  required?: boolean
  hint?: ReactNode
  error?: string
  warning?: string
  children: ReactNode
  full?: boolean
}

export function FieldShell({ label, required, hint, error, warning, children, full }: FieldShellProps) {
  return (
    <div className={styles.formGroup} style={full ? { gridColumn: '1/-1' } : undefined}>
      <label className={styles.label}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      {children}
      {error ? <span className={styles.errorMsg}>{error}</span>
        : warning ? <span className={styles.errorMsg} style={{ color: 'var(--aptean-warning)' }}>{warning}</span>
        : hint ? <div className={styles.fieldHint}>{hint}</div> : null}
    </div>
  )
}

interface TextFieldProps extends Omit<FieldShellProps, 'children'> {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  mono?: boolean
  readOnly?: boolean
}

export function TextField({ value, onChange, placeholder, mono, readOnly, ...shell }: TextFieldProps) {
  return (
    <FieldShell {...shell}>
      <input
        className={clsx(styles.input, shell.error && styles.inputError)}
        style={mono ? { fontFamily: 'var(--aptean-font-mono)', fontSize: 13 } : undefined}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldShell>
  )
}

interface NumberFieldProps extends Omit<FieldShellProps, 'children'> {
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  readOnly?: boolean
}

export function NumberField({ value, onChange, min, max, readOnly, ...shell }: NumberFieldProps) {
  return (
    <FieldShell {...shell}>
      <input
        type="number"
        className={clsx(styles.input, shell.error && styles.inputError)}
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value === '' ? NaN : Number(e.target.value))}
      />
    </FieldShell>
  )
}

export interface ChoiceOption<T extends string> {
  value: T
  label: string
  disabled?: boolean
}

interface SelectFieldProps<T extends string> extends Omit<FieldShellProps, 'children'> {
  value: T
  options: ChoiceOption<T>[]
  onChange: (v: T) => void
}

export function SelectField<T extends string>({ value, options, onChange, ...shell }: SelectFieldProps<T>) {
  return (
    <FieldShell {...shell}>
      <select className={clsx(styles.select, shell.error && styles.inputError)} value={value}
        onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
    </FieldShell>
  )
}

interface RadioFieldProps<T extends string> extends Omit<FieldShellProps, 'children'> {
  name: string
  value: T
  options: ChoiceOption<T>[]
  onChange: (v: T) => void
}

export function RadioField<T extends string>({ name, value, options, onChange, ...shell }: RadioFieldProps<T>) {
  return (
    <FieldShell {...shell}>
      <div className={styles.radioGroup}>
        {options.map((o) => (
          <label key={o.value} className={clsx(styles.radioOption, value === o.value && styles.radioOptionSelected)}
            style={o.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}>
            <input type="radio" name={name} checked={value === o.value} disabled={o.disabled}
              onChange={() => onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
    </FieldShell>
  )
}

export function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      style={disabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
      className={clsx(styles.toggle, checked && styles.toggleOn)}
      onClick={() => { if (!disabled) onChange(!checked) }}
    >
      <span className={styles.toggleThumb} />
    </button>
  )
}

interface ToggleFieldProps {
  label: string
  description?: ReactNode
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  error?: string
}

export function ToggleField({ label, description, checked, onChange, disabled, error }: ToggleFieldProps) {
  return (
    <div style={{ gridColumn: '1/-1' }}>
      <div className={styles.toggleRow}>
        <div>
          <span className={styles.toggleLabel}>{label}</span>
          {description && <span className={styles.toggleDesc}>{description}</span>}
        </div>
        <Toggle checked={checked} onChange={onChange} disabled={disabled} />
      </div>
      {error && <span className={styles.errorMsg}>{error}</span>}
    </div>
  )
}

export function ReadOnlyField({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className={styles.formGroup}>
      <label className={styles.label}>{label}</label>
      <div className={styles.vnetInfoBox}>
        <span className={styles.vnetName}>{value}</span>
        {hint && <span className={styles.vnetHint}>{hint}</span>}
      </div>
    </div>
  )
}

export function SubHeading({ children }: { children: ReactNode }) {
  return (
    <div style={{ gridColumn: '1/-1', marginTop: 8, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--aptean-text-secondary)', borderTop: '1px solid var(--aptean-border)', paddingTop: 14 }}>
      {children}
    </div>
  )
}
